import React, {useContext, useEffect, useState} from 'react';
import {Button, Col, Divider, Form, Input, Modal, Row, Select, Space, Table, Tag} from 'antd';
import {CaretDownOutlined, CloseOutlined, EnvironmentOutlined, TeamOutlined, UserOutlined} from '@ant-design/icons';
import {FormattedMessage, useIntl} from 'react-intl';
import {useSearchParams} from 'react-router-dom';
import {getAuthenticationApi, getOrganizationsApi, getUserApi} from '../api/apiInstances';
import {AuthUserResponse, OrganizationMembershipResponse} from '../api-client';
import {tokenService} from '../services/tokenService';
import {allCountries} from '../i18n/countries';
import logger from '../utils/logger';
import DetailWrapper from '../components/common/DetailWrapper';
import FormButtons from '../components/common/FormButtons';
import {isMobileOnly} from 'react-device-detect';
import {AlertContext} from '../context/AlertContext';
import '../assets/css/Dentobook.css';

const statusTagColor = (status: string): string => {
    switch (status?.toLowerCase()) {
        case 'active': return 'success';
        case 'invited': return 'warning';
        case 'suspended': return 'error';
        default: return 'default';
    }
};

const ProfilePage = () => {
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const {setWarning} = useContext(AlertContext);

    const [user, setUser] = useState<AuthUserResponse | null>(null);
    const [memberships, setMemberships] = useState<OrganizationMembershipResponse[]>([]);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);
    const [orgName, setOrgName] = useState('');
    const [orgCountry, setOrgCountry] = useState('');
    const [creatingOrg, setCreatingOrg] = useState(false);

    const [form] = Form.useForm();

    useEffect(() => {
        getAuthenticationApi().getCurrentUserInfo()
            .then(res => {
                setUser(res.data);
                form.setFieldsValue({
                    firstName: res.data.firstName ?? '',
                    lastName: res.data.lastName ?? '',
                    phone: res.data.phone ?? '',
                });
                if (searchParams.get('createOrg') === '1') {
                    if (res.data.firstName?.trim() && res.data.lastName?.trim()) {
                        setCreateOrgModalOpen(true);
                    } else {
                        setWarning(intl.formatMessage({id: 'complete_profile_first'}));
                    }
                }
            })
            .catch(err => logger.error('Failed to load user info:', err));

        getAuthenticationApi().getUserOrganizations()
            .then(res => setMemberships(res.data))
            .catch(err => logger.error('Failed to load memberships:', err));
    }, []);

    const handleSave = async () => {
        if (!user) return;
        setSaving(true);
        try {
            await form.validateFields();
            const values = form.getFieldsValue();
            await getUserApi().updateUser(user.id, {
                firstName: values.firstName || null,
                lastName: values.lastName || null,
                phone: values.phone || null,
            });
            setUser(prev => prev ? {...prev, ...values} : prev);
            setEditing(false);
        } catch (err: any) {
            logger.error('Failed to save profile:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setEditing(false);
        form.setFieldsValue({
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            phone: user?.phone ?? '',
        });
    };

    const handleOpenCreateOrgModal = () => {
        if (!user?.firstName?.trim() || !user?.lastName?.trim()) {
            setWarning(intl.formatMessage({id: 'complete_profile_first'}));
            return;
        }
        setCreateOrgModalOpen(true);
    };

    const handleCreateOrg = async () => {
        if (!orgName.trim()) return;
        setCreatingOrg(true);
        try {
            const res = await getOrganizationsApi().createOrganizationSelf({
                name: orgName.trim(),
                country: orgCountry || undefined,
            });
            tokenService.setTokens(res.data.access_token, res.data.refresh_token);
            window.location.href = '/';
        } catch (err: any) {
            logger.error('Failed to create organization:', err);
            const detail = err?.response?.data?.detail;
            if (typeof detail === 'string' && detail.toLowerCase().includes('complete your profile')) {
                setWarning(intl.formatMessage({id: 'complete_profile_first'}));
                setCreateOrgModalOpen(false);
            } else {
                setWarning(intl.formatMessage({id: 'unexpected_error'}));
            }
            setCreatingOrg(false);
        }
    };

    const currentOrgId = tokenService.getOrganizationIdFromToken();
    const hasClinicAdminMembership = memberships.some(
        m => m.role === 'CLINIC_ADMIN' && m.status?.toLowerCase() === 'active'
    );

    const columns = [
        {
            title: intl.formatMessage({id: 'organization_name'}),
            dataIndex: 'organizationName',
            key: 'organizationName',
            render: (name: string, record: OrganizationMembershipResponse) => (
                <Space size="small">
                    <span className="body-large-medium">{name}</span>
                    {record.organizationId === currentOrgId && (
                        <Tag color="blue"><FormattedMessage id="active_org"/></Tag>
                    )}
                </Space>
            ),
        },
        {
            title: intl.formatMessage({id: 'role'}),
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => (
                <span className="body-large-regular">
                    {role ? <FormattedMessage id={role}/> : role}
                </span>
            ),
        },
        {
            title: intl.formatMessage({id: 'status'}),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={statusTagColor(status)}>
                    {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : status}
                </Tag>
            ),
        },
    ];

    const createOrgModalFooter = (
        <div className="modal-btn-wrapper">
            <Button className="modal-btn" onClick={() => {setCreateOrgModalOpen(false); setOrgName(''); setOrgCountry('');}}>
                <FormattedMessage id="cancel"/>
            </Button>
            <Button
                className="modal-btn"
                type="primary"
                loading={creatingOrg}
                disabled={!orgName.trim()}
                onClick={handleCreateOrg}
            >
                <FormattedMessage id="save"/>
            </Button>
        </div>
    );

    return (
        <DetailWrapper>
            <h3><FormattedMessage id="my_profile"/></h3>

            {/* ── My Details ──────────────────────────────────────────────── */}
            <h4>
                <span className="text-blue mr-8"><UserOutlined/></span>
                <FormattedMessage id="my_details"/>
            </h4>

            {editing ? (
                <Form form={form} layout="vertical">
                    <Row gutter={24}>
                        <Col md={12} xs={24}>
                            <Form.Item
                                name="firstName"
                                label={<FormattedMessage id="first_name"/>}
                                className="dentobook-form-item"
                                rules={[{required: true, message: intl.formatMessage({id: 'first_name_required'})}]}
                            >
                                <Input size="large"/>
                            </Form.Item>
                        </Col>
                        <Col md={12} xs={24}>
                            <Form.Item
                                name="lastName"
                                label={<FormattedMessage id="last_name"/>}
                                className="dentobook-form-item"
                                rules={[{required: true, message: intl.formatMessage({id: 'last_name_required'})}]}
                            >
                                <Input size="large"/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col md={12} xs={24}>
                            <Form.Item
                                className="dentobook-form-item"
                                label={<FormattedMessage id="email"/>}
                            >
                                <Input size="large" value={user?.email ?? ''} disabled/>
                            </Form.Item>
                        </Col>
                        <Col md={12} xs={24}>
                            <Form.Item
                                name="phone"
                                label={<FormattedMessage id="phone"/>}
                                className="dentobook-form-item"
                                rules={[
                                    {required: true, message: intl.formatMessage({id: 'phone_required'})},
                                    {pattern: /^[+()\d\s]+$/, message: intl.formatMessage({id: 'invalid_phone_number_text'})},
                                ]}
                            >
                                <Input size="large"/>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            ) : (
                <Space direction={isMobileOnly ? 'vertical' : 'horizontal'}>
                    <Space direction="vertical" className={isMobileOnly ? 'mb-2' : 'ml-6 mb-2 mt-2'}>
                        <span className="body-large-regular"><FormattedMessage id="first_name"/>:</span>
                        <span className="body-large-medium">
                            {user?.firstName ?? <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                    <Space direction="vertical" className={isMobileOnly ? 'mb-2' : 'ml-56 mb-2 mt-2'}>
                        <span className="body-large-regular"><FormattedMessage id="last_name"/>:</span>
                        <span className="body-large-medium">
                            {user?.lastName ?? <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                    <Space direction="vertical" className={isMobileOnly ? 'mb-2' : 'ml-56 mb-2 mt-2'}>
                        <span className="body-large-regular"><FormattedMessage id="email"/>:</span>
                        <span className="body-large-medium">
                            {user?.email ?? <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                    <Space direction="vertical" className={isMobileOnly ? 'mb-2' : 'ml-56 mb-2 mt-2'}>
                        <span className="body-large-regular"><FormattedMessage id="phone"/>:</span>
                        <span className="body-large-medium">
                            {user?.phone ?? <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Space>
            )}

            <Divider/>

            {/* ── Memberships ─────────────────────────────────────────────── */}
            <h4>
                <span className="text-blue mr-8"><TeamOutlined/></span>
                <FormattedMessage id="memberships"/>
            </h4>

            <Table
                dataSource={memberships}
                columns={columns}
                rowKey="membershipId"
                pagination={false}
                className="clinics-table"
            />

            {/* ── Sticky bottom action bar ─────────────────────────────────── */}
            <FormButtons>
                {editing ? (
                    <>
                        <Button
                            className="modal-btn"
                            size="large"
                            onClick={handleCancelEdit}
                        >
                            <FormattedMessage id="cancel"/>
                        </Button>
                        <Button
                            className="modal-btn"
                            type="primary"
                            size="large"
                            loading={saving}
                            onClick={handleSave}
                        >
                            <FormattedMessage id="save"/>
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            className={'edit-btn ' + (isMobileOnly ? 'mobile-update-btn' : 'dentobook-btn-primary')}
                            type="default"
                            size="large"
                            shape="round"
                            onClick={() => setEditing(true)}
                        >
                            <FormattedMessage id="edit"/>
                        </Button>
                        <Button
                            className={'edit-btn ' + (isMobileOnly ? 'mobile-update-btn' : 'dentobook-btn-primary')}
                            type="primary"
                            size="large"
                            shape="round"
                            onClick={handleOpenCreateOrgModal}
                        >
                            <FormattedMessage id={hasClinicAdminMembership ? 'create_another_organization' : 'create_my_organization'}/>
                        </Button>
                    </>
                )}
            </FormButtons>

            {/* ── Create org modal ─────────────────────────────────────────── */}
            <Modal
                transitionName=""
                className="dentobook-modal"
                title={<FormattedMessage id={hasClinicAdminMembership ? 'create_another_organization' : 'create_my_organization'}/>}
                open={createOrgModalOpen}
                footer={createOrgModalFooter}
                closeIcon={<CloseOutlined className="icon-24px" onClick={() => {setCreateOrgModalOpen(false); setOrgName(''); setOrgCountry('');}}/>}
                onCancel={() => {setCreateOrgModalOpen(false); setOrgName(''); setOrgCountry('');}}
            >
                <p className="body-large-regular" style={{marginBottom: 16}}>
                    <FormattedMessage id="create_org_modal_hint"/>
                </p>
                <Form layout="vertical">
                    <Form.Item
                        className="dentobook-form-item dentobook-simple-input"
                        label={<FormattedMessage id="organization_name"/>}
                        required
                    >
                        <Input
                            size="large"
                            value={orgName}
                            onChange={e => setOrgName(e.target.value)}
                            maxLength={250}
                            onPressEnter={handleCreateOrg}
                        />
                    </Form.Item>
                    <Form.Item
                        className="dentobook-form-item dentobook-simple-input single-select"
                        label={<FormattedMessage id="country"/>}
                    >
                        <Select
                            showSearch
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            autoComplete="new-state"
                            suffixIcon={<CaretDownOutlined/>}
                            className="dentobook-input"
                            value={orgCountry || undefined}
                            onChange={setOrgCountry}
                            allowClear
                            filterOption={(input, option) =>
                                String(option?.children).toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={intl.formatMessage({id: 'search'})}
                        >
                            {allCountries.map(c => (
                                <Select.Option key={c.code} value={c.code}>
                                    {c.name_en}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </DetailWrapper>
    );
};

export default ProfilePage;
